import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Input } from '../ui/input';
import { useDebouncedValue } from '../sales/useAbortableGet';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// SKU code input with server-side suggestions (code, product, old & marketplace codes).
// Combobox semantics: Arrow Up/Down move through suggestions, Enter picks, Escape closes.
export const SkuInput = ({ value, onPick, country, index }) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [active, setActive] = useState(-1);
  const query = useDebouncedValue(value, 300);
  const wrapper = useRef(null);
  const listId = `sku-input-options-${index}`;

  useEffect(() => {
    if (!open || query.trim().length < 2) { setOptions([]); return undefined; }
    const controller = new AbortController();
    axios.get(`${API}/skus/catalogue`, { params: { search: query.trim(), page_size: 10, country }, signal: controller.signal })
      .then(res => { setOptions(res.data.rows); setActive(-1); }).catch(() => {});
    return () => controller.abort();
  }, [query, open, country]);

  useEffect(() => {
    const close = event => { if (wrapper.current && !wrapper.current.contains(event.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const showList = open && options.length > 0;
  const pick = sku => { onPick({ sku_code: sku.sku_code, product_name: sku.product_name }); setOpen(false); setActive(-1); };

  const onKeyDown = event => {
    if (event.key === 'Escape') { setOpen(false); setActive(-1); return; }
    if (!showList) {
      if (event.key === 'ArrowDown') setOpen(true);
      return;
    }
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive(i => (i + 1) % options.length); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive(i => (i <= 0 ? options.length - 1 : i - 1)); }
    else if (event.key === 'Enter' && active >= 0 && options[active]) { event.preventDefault(); pick(options[active]); }
  };

  return (
    <div className="relative" ref={wrapper}>
      <Input value={value} onFocus={() => setOpen(true)} placeholder="SKU code"
        onChange={e => { onPick({ sku_code: e.target.value.toUpperCase(), product_name: '' }); setOpen(true); }}
        onKeyDown={onKeyDown}
        role="combobox" aria-autocomplete="list" aria-expanded={showList} aria-controls={showList ? listId : undefined}
        aria-activedescendant={showList && active >= 0 ? `${listId}-${active}` : undefined}
        aria-label={typeof index === 'number' ? `SKU code for line ${index + 1}` : undefined}
        className="h-9 font-mono text-[12px]" data-testid={`purchase-line-sku-${index}`} />
      {showList && (
        <div id={listId} role="listbox" aria-label="Matching SKUs" className="absolute z-50 mt-1 max-h-64 w-[min(380px,calc(100vw-2rem))] overflow-y-auto rounded-lg border bg-white shadow-lg" data-testid={`purchase-line-sku-options-${index}`}>
          {options.map((sku, i) => (
            <button type="button" key={sku.sku_code} id={`${listId}-${i}`} role="option" aria-selected={i === active} tabIndex={-1}
              className={`block w-full px-3 py-2 text-left text-[12px] hover:bg-blue-50 ${i === active ? 'bg-blue-50' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => pick(sku)}
              data-testid={`purchase-sku-option-${sku.sku_code}`}>
              <span className="font-mono font-semibold">{sku.sku_code}</span>
              <span className="ml-2 text-gray-500">{sku.product_name}</span>
              {sku.bundle_of && <span className="ml-2 rounded bg-emerald-50 px-1 text-[10px] text-emerald-700">Bundle {sku.bundle_qty}× {sku.bundle_of}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
